/**
 * Per-type TUI cell adapters — what a terminal grid draws for a cell whose
 * value is not a scalar, so the TUI shows what the DOM binds instead of
 * `<object>`: the DOM's per-type plugins (the column event list, the task
 * column, the data-json and weather cells, the graph and canvas marks) each
 * draw their own shape, and each has a TUI adapter here.
 *
 *   event    `HH:MM title` (all-day: the title) and `+n` for the column's
 *            other entries — the column event list's first line
 *   task     `HH:MM title` from the scheduled start (else the due time), `✓`
 *            before a done task, `+n` for the column's other tasks
 *   weather  a reading: the weather cell's day (`72°F ↑80 ↓61 Sunny`: the mean
 *            in °F, the high and low, the condition — what its tooltip and
 *            condition glyph show), humidity and UV when present, or a scalar reading (`19° precip 1.5`),
 *            and `+n` for the column's other readings
 *   obj      compact `key:value` pairs (the id left out), nested objects as `{…}`
 *   sticker  the sticker's message, else its name (`sun`) — what the sticker
 *            column's list shows — and `+n` for the column's other stickers
 *   spark    sparkline glyphs `▁▂▃▄▅▆▇█` over a list of numbers (a graph or
 *            canvas series), scaled to the list's own min…max
 *   scalar   no adapter: the grid's own text / number / checkmark formatting
 *
 * An adapter reads the cell node and, through a `TuiCellSource`, every
 * entry the row holds in the node's column (the scene carries the first; a
 * caller that can read the cache supplies the rest — the CLI's
 * `columnFactsCellSource`) and the zone times are shown in (the viewer's).
 * Pure: no DOM, no load, no store write.
 */

import type { Option } from "../core/option.ts";
import { DateTime, MsSinceEpoch, type TimezoneRegion, Utc } from "@tento-chrono";
import type { CellEntry } from "../core/dtype.ts";
import type { CellNode, RowOverlayNode } from "../render/scene.ts";
import type { ColumnKey } from "../render/layout-types.ts";
import { fitText, oneLine, textWidth } from "./text-cells.ts";
import { DATA_CAP } from "./tui-grid-fit.ts";

/** Which adapter draws a cell (`scalar`: none — the grid's own formatting). */
export type TuiCellKind = "event" | "task" | "weather" | "obj" | "sticker" | "spark" | "scalar";

/** How an adapter prints an instant: its wall-clock `HH:MM` in the viewer's zone. */
export interface TuiClock {
  /** `HH:MM` of the instant `msSinceEpoch` (milliseconds since the Unix epoch). */
  hhmm(msSinceEpoch: number): string;
}

/** What an adapter may read besides the node: the column's entries and the viewer's clock. */
export interface TuiCellSource {
  /** Every entry the node's row holds in the node's column, first one first (at least the node's own). */
  entries(node: CellNode): readonly CellEntry[];
  /** How times print (the viewer's zone); `null`: UTC. */
  readonly clock: Option<TuiClock>;
  /**
   * What `entries` reads for a row beyond the row's scene nodes (a frame's column facts), by identity: a grid keeps a
   * row's cached line only while this is the same object (`TuiGridOptions.rowInputs`); `null` when the nodes are all
   * it reads.
   */
  rowInputs(row: CellNode["key"]["row"]): Option<object>;
}

/** A grid's per-cell text hook (`TuiGridOptions.cellText`): the text for `node` in at most `width` columns, or `null` for the grid's own. */
export type TuiCellText = (node: CellNode, width: number) => Option<string>;

/** The fields a sticker's value holds (`StickerCellData`; a short-format fixture writes `sticker`). */
const STICKER_FIELDS: ReadonlySet<string> = new Set(["stickerId", "sticker", "message", "effect", "x", "id"]);

/** Whether `value` is a sticker: an object naming one (`stickerId`, `sticker`) and holding nothing a sticker does not. */
function isStickerValue(value: unknown): boolean {
  const obj = objectOf(value);
  if (obj == null || stringAt(obj, "stickerId", "sticker") == null) return false;
  return Object.keys(obj).every((key) => STICKER_FIELDS.has(key));
}

/** The widest text an adapter asks the grid for (a column grows to fit, up to this): a data column's widest. */
export const TUI_CELL_MAX_WIDTH: number = DATA_CAP;

/** Sparkline glyphs, lowest to highest. */
const SPARKS = "▁▂▃▄▅▆▇█";

/** The node's own value (the scene's entry), or `null`. */
function valueOf(node: CellNode): unknown {
  const content = node.content;
  return content != null && content.kind === "value" ? content.entry?.value ?? null : null;
}

/** A plain object value (not a list, not null). */
function objectOf(value: unknown): Option<Readonly<Record<string, unknown>>> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value)) out[key] = Reflect.get(value, key);
  return out;
}

function stringAt(obj: Readonly<Record<string, unknown>>, ...keys: readonly string[]): Option<string> {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

/** `text` cut to `width` terminal columns (an ellipsis marks the cut; a wide character or an emoji is never split). */
function fit(text: string, width: number): string {
  return width <= 0 ? "" : fitText(text, width);
}

/** An instant a value field holds: an ISO datetime string (wire JSON) or a `DateTime` (a decoded domain value). */
function instantOf(v: unknown): Option<DateTime<Utc>> {
  if (v instanceof DateTime) return v.toUtc();
  if (typeof v !== "string" || v.length === 0) return null;
  const parsed = DateTime.fromRfc3339(v);
  return parsed.isOk ? parsed.exp().toUtc() : null;
}

/** The first of `keys` that holds an instant (`instantOf`). */
function instantAt(obj: Readonly<Record<string, unknown>>, ...keys: readonly string[]): Option<DateTime<Utc>> {
  for (const key of keys) {
    const at = instantOf(obj[key]);
    if (at != null) return at;
  }
  return null;
}

/** A `TuiClock` in `zone` (a host builds one from the viewer's preferred zone). */
export function tuiClock(zone: TimezoneRegion): TuiClock {
  return { hhmm: (ms) => hhmmOf(zone.toTz(DateTime.fromMse(MsSinceEpoch.of(ms), Utc))) };
}

function hhmmOf(at: DateTime<string>): string {
  const t = at.time;
  return `${String(t.hrs).padStart(2, "0")}:${String(t.mins).padStart(2, "0")}`;
}

/** `HH:MM` of `at` on `clock` (UTC when `null`); `null` when there is no instant. */
export function clockOf(at: Option<DateTime<Utc>>, clock: Option<TuiClock>): Option<string> {
  if (at == null) return null;
  return clock == null ? hhmmOf(at.toUtc()) : clock.hhmm(at.mse);
}

/** Whether `value` reads as an event (`event.v1`: a title and a start, or its short keys). */
export function isEventValue(value: unknown): boolean {
  const obj = objectOf(value);
  return obj != null && stringAt(obj, "title", "t") != null && !("scheduledStart" in obj) &&
    (instantAt(obj, "start", "st") != null || stringAt(obj, "startDate", "sd") != null || obj["startDate"] != null);
}

/** Whether `value` reads as a task (a title and a task field). */
export function isTaskValue(value: unknown): boolean {
  const obj = objectOf(value);
  return obj != null && stringAt(obj, "title") != null &&
    ["scheduledStart", "dueDate", "status", "completedAt", "importance"].some((k) => k in obj);
}

/** A finite number, or `null`. */
function numberAt(obj: Readonly<Record<string, unknown>>, key: string): Option<number> {
  const v = obj[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** `n` rounded to at most one decimal. */
function rounded(n: number): string {
  return String(Math.round(n * 10) / 10);
}

/**
 * The weather cell's condition names by code (its glyph table, `WeatherDay.conditionEmoji`), for a day that carries a
 * code but no text.
 */
const WEATHER_CONDITIONS: ReadonlyMap<number, string> = new Map([
  [1000, "Sunny"],
  [1003, "Partly cloudy"],
  [1006, "Cloudy"],
  [1009, "Overcast"],
  [1063, "Rain"],
  [1186, "Rain"],
  [1189, "Rain"],
  [1192, "Rain"],
  [1240, "Rain"],
  [1243, "Rain"],
]);

/**
 * A weather reading as text, or `null` when `value` is none: the day-level reading the weather cell binds
 * (`{ day: { avgtempF, maxtempF, mintempF, condition } }`) as `72°F ↑80 ↓61 Sunny`, a field it lacks left out; the
 * scalar reading (`{ temperature, observedAt, precipitation }`, which names no unit) as `19°`, with ` precip 1.5` when it
 * rained.
 */
function weatherText(value: unknown): Option<string> {
  const obj = objectOf(value);
  if (obj == null) return null;
  const day = objectOf(obj["day"]);
  if (day != null) {
    const avg = numberAt(day, "avgtempF");
    const hi = numberAt(day, "maxtempF");
    const lo = numberAt(day, "mintempF");
    const humidity = numberAt(day, "avghumidity");
    const uv = numberAt(day, "uv");
    const condition = objectOf(day["condition"]);
    const code = condition == null ? null : numberAt(condition, "code");
    const named = condition == null ? null : stringAt(condition, "text") ?? (code == null ? null : WEATHER_CONDITIONS.get(code) ?? null);
    const parts = [
      ...(avg != null ? [`${rounded(avg)}°F`] : []),
      ...(hi != null ? [`↑${rounded(hi)}`] : []),
      ...(lo != null ? [`↓${rounded(lo)}`] : []),
      ...(named != null ? [named.trim()] : []),
      ...(humidity != null ? [`humidity ${rounded(humidity)}%`] : []),
      ...(uv != null ? [`UV ${rounded(uv)}`] : []),
    ];
    return parts.length === 0 ? null : parts.join(" ");
  }
  const temperature = numberAt(obj, "temperature");
  if (temperature == null) return null;
  const precipitation = numberAt(obj, "precipitation");
  return `${rounded(temperature)}°${precipitation != null && precipitation > 0 ? ` precip ${rounded(precipitation)}` : ""}`;
}

/** A list of finite numbers (a graph / canvas series), or `null`. */
function seriesOf(value: unknown): Option<readonly number[]> {
  if (!Array.isArray(value) || value.length === 0) return null;
  const out: number[] = [];
  for (const v of value) {
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
    out.push(v);
  }
  return out;
}

/** ` +n` for the column's other entries (empty when the node's is the only one). */
function more(node: CellNode, source: Option<TuiCellSource>): string {
  const n = source == null ? 0 : source.entries(node).length - 1;
  return n > 0 ? ` +${n}` : "";
}

/** A line of text ending in `suffix`, fitted to `width` (the suffix kept whole). */
function withSuffix(text: string, suffix: string, width: number): string {
  if (suffix.length === 0) return fit(text, width);
  return `${fit(text, Math.max(width - textWidth(suffix), 1))}${suffix}`;
}

/** The TUI adapters, by kind (module doc). */
export namespace TuiCellAdapters {
  /**
   * ` +n` for the column's other entries — what an adapter's text ends in, and a person's grid's scalar suffix
   * (`TuiGridOptions.scalarSuffix`); empty when the node's entry is the column's only one.
   */
  export function moreSuffix(node: CellNode, source: Option<TuiCellSource>): string {
    return more(node, source);
  }

  /** An event: `HH:MM title` (all-day: the title) and `+n`. */
  export function event(node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    const obj = objectOf(valueOf(node));
    if (obj == null) return null;
    const title = stringAt(obj, "title", "t") ?? "(untitled)";
    // An all-day event (a start date) shows its title; a timed one its start in the viewer's zone.
    const allDay = stringAt(obj, "startDate", "sd") != null;
    const clock = allDay ? null : clockOf(instantAt(obj, "start", "st"), source?.clock ?? null);
    const text = clock == null ? title : `${clock} ${title}`;
    return withSuffix(text, more(node, source), width);
  }

  /** A task: `✓` when done, `HH:MM` of its scheduled start (else due), the title, and `+n`. */
  export function task(node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    const obj = objectOf(valueOf(node));
    if (obj == null) return null;
    const title = stringAt(obj, "title") ?? "(untitled)";
    const done = obj["status"] === "done" || obj["status"] === "completed" || instantAt(obj, "completedAt") != null;
    const clock = clockOf(instantAt(obj, "scheduledStart", "dueDate"), source?.clock ?? null);
    const text = `${done ? "✓ " : ""}${clock != null ? `${clock} ` : ""}${title}`;
    return withSuffix(text, more(node, source), width);
  }

  /** A weather reading (`weatherText`) and `+n`; a value that is no reading draws as an object (`obj`). */
  export function weather(node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    const text = weatherText(valueOf(node));
    return text == null ? obj(node, width, source) : withSuffix(text, more(node, source), width);
  }

  /**
   * An object: its label when it has one (`title`, `summary`, `label`, `name` or `text`, the fields `lona cells.get`
   * prints an object by), never key names; else `key:value` pairs, the id left out, nested values as `{…}` / `[n]`.
   * A list: `n× ` and its first item.
   */
  export function obj(node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    const raw = valueOf(node);
    if (Array.isArray(raw)) {
      const first = objectOf(raw[0]);
      const label = first == null ? String(raw[0] ?? "") : stringAt(first, "label", "title", "name", "id") ?? "{…}";
      return withSuffix(`${raw.length}× ${label}`, more(node, source), width);
    }
    const value = objectOf(raw);
    if (value == null) return null;
    const label = stringAt(value, "title", "summary", "label", "name", "text");
    if (label != null) return withSuffix(oneLine(label), more(node, source), width);
    const parts: string[] = [];
    for (const [key, v] of Object.entries(value)) {
      if (key === "id") continue;
      const text = v == null
        ? "·"
        : Array.isArray(v)
        ? `[${v.length}]`
        : typeof v === "object"
        ? "{…}"
        : typeof v === "number"
        ? String(Math.round(v * 100) / 100)
        : String(v);
      parts.push(`${key}:${text}`);
    }
    return withSuffix(parts.length === 0 ? "{}" : parts.join(" "), more(node, source), width);
  }

  /**
   * A sticker: its message, else its catalog name (`stickerId`; a short-format fixture's `sticker`), as the sticker
   * column lists it — never the `sticker:sun` of its fields; any other value draws as an object.
   */
  export function sticker(node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    const value = objectOf(valueOf(node));
    const name = value == null ? null : stringAt(value, "message") ?? stringAt(value, "stickerId", "sticker");
    return name == null ? obj(node, width, source) : withSuffix(name, more(node, source), width);
  }

  /** A series: one sparkline glyph per number (the latest `width`), scaled to the series' min…max. */
  export function spark(node: CellNode, width: number): Option<string> {
    const series = seriesOf(valueOf(node));
    if (series == null) return null;
    return sparkline(series.slice(-Math.max(width, 1)));
  }

  /** The adapter of `kind`; `scalar` and a value the adapter does not read answer `null` (the grid's own text). */
  export function text(kind: TuiCellKind, node: CellNode, width: number, source: Option<TuiCellSource>): Option<string> {
    switch (kind) {
      case "event":
        return event(node, width, source);
      case "task":
        return task(node, width, source);
      case "weather":
        return weather(node, width, source);
      case "obj":
        return obj(node, width, source);
      case "sticker":
        return sticker(node, width, source);
      case "spark":
        return spark(node, width);
      case "scalar":
        return null;
    }
  }

  /**
   * The kind a value reads as when its renderer names none (a data-json
   * column holding events, a text cell holding a list, a stickers row's
   * source): a sticker, an event, a task, a number series, any other object;
   * `scalar` otherwise.
   */
  export function kindOfValue(value: Option<CellEntry["value"]>): TuiCellKind {
    if (isStickerValue(value)) return "sticker";
    if (isEventValue(value)) return "event";
    if (isTaskValue(value)) return "task";
    if (seriesOf(value) != null) return "spark";
    if (objectOf(value) != null || Array.isArray(value)) return "obj";
    return "scalar";
  }
}

/** Sparkline glyphs for `series` (flat series: the middle glyph). */
export function sparkline(series: readonly number[]): string {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of series) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const top = SPARKS.length - 1;
  return series.map((v) => {
    const i = hi === lo ? Math.floor(top / 2) : Math.round(((v - lo) / (hi - lo)) * top);
    return SPARKS[Math.min(Math.max(i, 0), top)] ?? "·";
  }).join("");
}

/**
 * The overlay strip under a row: per column, `⊞n` — the intents of the
 * row's overlay nodes (spanned event bars, all-day strips, overflow chips)
 * whose range meets the column's period; blank where none does.
 */
export function overlayCounts<K extends ColumnKey>(
  overlays: readonly RowOverlayNode[],
  columns: readonly K[],
): readonly number[] {
  return columns.map((col) => {
    let n = 0;
    for (const node of overlays) {
      for (const intent of node.intents) {
        // A zero-length intent (`[d, d)`: one day's chip) meets the column holding `d`.
        const start = intent.range.start.dse;
        const end = Math.max(intent.range.end.dse, start + 1);
        if (start < col.pd.end.dse && col.pd.start.dse < end) n++;
      }
    }
    return n;
  });
}
